import { useLingui as _useLingui } from "../lib/i18n/useLingui";
import { Button } from "../components/Button";
import { ContentMessage } from "../components/ContentMessage";
import { Icon } from "../components/Icon";
import { Input } from "../components/Input";
import { PopoverContent, PopoverRoot, PopoverTrigger } from "../components/Popover";
import { ScrollArea, ScrollBar } from "../components/ScrollArea";
import { Spinner } from "../components/Spinner";
import { CheckDone01, SearchMd, XClose } from "../icons/v2-stroke";
import { formatNumber, useFormatLocale } from "../lib/i18n/format";
import { cn } from "../lib/utils";
import React, { forwardRef, useEffect, useRef, useState } from "react";
/**
 * A search-specific text field with a built-in search icon and clear affordance, wired
 * through a simplified `value` / `onChange` (string) contract. Use it for freeform search
 * or filter input; `onChange` receives the raw string value, so manage the query in state
 * directly. For generic short text or number entry use `Input`; for multi-line input use
 * `TextArea`; to surface live results inline, use `SearchInputWithPopover`.
 *
 * @summary Search text field with clear affordance.
 */
export const SearchInput = forwardRef(({
  placeholder,
  id,
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  name,
  disabled = false,
  isLoading = false,
  className
}, ref) => {
  const {
    _: _t
  } = _useLingui();
  const clearInputField = () => {
    onChange("");
  };
  const inputId = id !== null && id !== void 0 ? id : name;
  return /*#__PURE__*/React.createElement("div", {
    className: cn("relative", className)
  }, /*#__PURE__*/React.createElement(Input, {
    id: inputId,
    type: "text",
    name: name,
    autoComplete: "off",
    placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : _t(
    /** i18n */
    {
      id: "A1taO8",
      message: "Search"
    }),
    value: value,
    onChange: e => {
      onChange(e.target.value);
    },
    onFocus: onFocus,
    onBlur: onBlur,
    onKeyDown: onKeyDown,
    disabled: disabled,
    ref: ref
    // compta : la loupe (ou la croix) est en surimpression à droite, le texte s'arrête avant.
    ,
    className: "pr-8"
  }), /*#__PURE__*/React.createElement("div", {
    className: "absolute inset-y-0 right-0 flex items-center pr-1"
  }, isLoading ? /*#__PURE__*/React.createElement("div", {
    className: "px-1"
  }, /*#__PURE__*/React.createElement(Spinner, {
    size: "xs"
  })) : value ? /*#__PURE__*/React.createElement(Button, {
    icon: XClose,
    tooltip: _t(
    /** i18n */
    {
      id: "V8yTm6",
      message: "Clear search"
    }),
    variant: "ghost",
    size: "xs",
    onClick: clearInputField
  }) : /*#__PURE__*/React.createElement("div", {
    className: cn("px-2", disabled ? "text-muted-foreground" : "text-foreground")
  }, /*#__PURE__*/React.createElement(Icon, {
    visual: SearchMd,
    size: "xs",
    className: "text-muted-foreground"
  }))));
});
SearchInput.displayName = "SearchInput";
const MAX_HEIGHT_CLASSES = {
  sm: "max-h-48",
  md: "max-h-72",
  lg: "max-h-96",
  xl: "max-h-[40rem]"
};
function BaseSearchInputWithPopover({
  items,
  renderItem,
  onItemSelect,
  onSelectAll,
  contentClassName,
  className,
  open,
  onOpenChange,
  value,
  onChange,
  mountPortal,
  mountPortalContainer,
  noResults,
  isLoading,
  contentMessage,
  displayItemCount = false,
  totalItems,
  stickyTopContent,
  stickyBottomContent,
  availableHeight = false,
  maxHeight = "md",
  ...searchInputProps
}, ref) {
  const {
    _: _t2
  } = _useLingui();
  const formatLocale = useFormatLocale();
  const itemCount = items.length;
  // Plural forms are picked with `itemCount`, but `#` would format it in the catalog's locale.
  const formattedItemCount = formatNumber(itemCount, undefined, formatLocale);
  const formattedTotalItems = totalItems === undefined ? undefined : formatNumber(totalItems, undefined, formatLocale);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef([]);
  const showHeader = Boolean(stickyTopContent) || items.length > 0 && (displayItemCount || onSelectAll);
  const showBottom = Boolean(stickyBottomContent);
  useEffect(() => {
    itemRefs.current = new Array(items.length).fill(null);
  }, [items.length]);
  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);
  const handleKeyDown = e => {
    if (!open || !items.length) {
      return;
    }
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(current => {
          var _itemRefs$current$new;
          const newIndex = (current - 1 + items.length) % items.length;
          (_itemRefs$current$new = itemRefs.current[newIndex]) === null || _itemRefs$current$new === void 0 || _itemRefs$current$new.scrollIntoView({
            block: "nearest"
          });
          return newIndex;
        });
        break;
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(current => {
          var _itemRefs$current$new2;
          const newIndex = (current + 1) % items.length;
          (_itemRefs$current$new2 = itemRefs.current[newIndex]) === null || _itemRefs$current$new2 === void 0 || _itemRefs$current$new2.scrollIntoView({
            block: "nearest"
          });
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        onOpenChange(false);
        if (items[selectedIndex] && onItemSelect) {
          onItemSelect(items[selectedIndex]);
        }
        break;
    }
  };
  return /*#__PURE__*/React.createElement(PopoverRoot, {
    modal: false,
    open: open,
    onOpenChange: onOpenChange
  }, /*#__PURE__*/React.createElement(PopoverTrigger, {
    asChild: true
  }, /*#__PURE__*/React.createElement(SearchInput, {
    ref: ref,
    className: cn("w-full", className),
    value: value,
    onChange: newValue => {
      onChange === null || onChange === void 0 || onChange(newValue);
      if (newValue && !open) {
        onOpenChange(true);
      }
    },
    ...searchInputProps,
    onKeyDown: handleKeyDown,
    "aria-expanded": open,
    "aria-haspopup": "listbox",
    "aria-controls": "search-popover-content"
  })), /*#__PURE__*/React.createElement(PopoverContent, {
    className: cn("w-(--radix-popover-trigger-width) rounded-lg border border-border bg-background shadow-lg", availableHeight && "max-h-(--radix-popover-content-available-height) overflow-hidden", contentClassName),
    sideOffset: 0,
    fullWidth: true,
    align: "start",
    id: "search-popover-content",
    onOpenAutoFocus: e => e.preventDefault(),
    onCloseAutoFocus: e => e.preventDefault(),
    onInteractOutside: () => onOpenChange(false),
    mountPortal: mountPortal,
    mountPortalContainer: mountPortalContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: cn("flex flex-col overflow-hidden rounded-lg", availableHeight && "max-h-full")
  }, showHeader && /*#__PURE__*/React.createElement("div", {
    className: cn("z-10 flex shrink-0 items-center justify-between gap-2 border-b border-border bg-background p-2"),
    onMouseDown: e => e.preventDefault()
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-1 items-center gap-2"
  }, stickyTopContent, displayItemCount && items.length > 0 && /*#__PURE__*/React.createElement("span", {
    className: "text-sm text-muted-foreground"
  }, totalItems ? _t2(
  /** i18n */
  {
    id: "bWK3OK",
    message: "{itemCount, plural, one {{formattedItemCount} search result} other {{formattedItemCount} search results}} (out of {formattedTotalItems}).",
    values: {
      itemCount: itemCount,
      formattedItemCount: formattedItemCount,
      formattedTotalItems: formattedTotalItems
    }
  }) : _t2(
  /** i18n */
  {
    id: "NBvjpX",
    message: "{itemCount, plural, one {{formattedItemCount} search result.} other {{formattedItemCount} search results.}}",
    values: {
      itemCount: itemCount,
      formattedItemCount: formattedItemCount
    }
  }))), onSelectAll && items.length > 0 && /*#__PURE__*/React.createElement(Button, {
    variant: "ghost",
    size: "xs",
    onClick: onSelectAll,
    label: _t2(
    /** i18n */
    {
      id: "wgNoIs",
      message: "Select all"
    }),
    icon: CheckDone01
  })), /*#__PURE__*/React.createElement(ScrollArea, {
    className: cn("flex min-h-0 flex-1 flex-col", availableHeight ? "max-h-[calc(var(--radix-popover-content-available-height)-12px)]" : MAX_HEIGHT_CLASSES[maxHeight]),
    hideScrollBar: true
  }, /*#__PURE__*/React.createElement("div", {
    role: "listbox",
    className: "flex flex-col"
  }, items.length > 0 ? items.map((item, index) => /*#__PURE__*/React.createElement("div", {
    key: index,
    ref: el => itemRefs.current[index] = el,
    onClick: () => {
      onOpenChange(false);
      onItemSelect === null || onItemSelect === void 0 || onItemSelect(item);
    }
  }, renderItem(item, selectedIndex === index))) : isLoading ? /*#__PURE__*/React.createElement("div", {
    className: "flex justify-center py-8"
  }, /*#__PURE__*/React.createElement(Spinner, {
    variant: "dark",
    size: "md"
  })) : /*#__PURE__*/React.createElement("div", {
    className: "p-4 text-center text-sm italic text-muted-foreground"
  }, noResults !== null && noResults !== void 0 ? noResults : "")), /*#__PURE__*/React.createElement(ScrollBar, {
    className: "py-0"
  })), showBottom && /*#__PURE__*/React.createElement("div", {
    className: cn("z-10 hidden shrink-0 items-center justify-between gap-2 border-t border-border bg-background p-2 sm:flex")
  }, stickyBottomContent), contentMessage && /*#__PURE__*/React.createElement("div", {
    className: "p-1"
  }, /*#__PURE__*/React.createElement(ContentMessage, {
    ...contentMessage
  })))));
}

/**
 * A `SearchInput` with an attached results dropdown offering keyboard navigation, custom
 * `renderItem`, an optional `onSelectAll`, sticky top/bottom content, item counts, and a
 * `noResults` state. Use it when search results should appear inline below the field;
 * handle selection via `onItemSelect`. For a plain field without results, use `SearchInput`.
 *
 * @summary Search field with results popover.
 */
export const SearchInputWithPopover = forwardRef(BaseSearchInputWithPopover);
//# sourceMappingURL=SearchInput.js.map